import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
    REFERENCE_BOOKS_URL,
    REFERENCE_TABLE_URL,
    getFetchRequest,
} from '../../store/requests';
import { errorCallback, makeUrl } from '../../util';
import Title from '../Title';
import Loading from '../Loading';
import { Button, Input } from '@chapche/ui';
import { SearchIcon } from '../../appearence/icons/search_icon';

const PAGE_SIZE = 50;

type Translate = TFunction;

type ReferenceSchemaColumn = {
    name: string;
    type?: string;
    unit?: string;
    maps_to?: string;
    description?: string;
};

type ReferenceSchemaJson = {
    columns?: ReferenceSchemaColumn[];
    basis?: string;
    [key: string]: unknown;
};

type ReferenceBook = {
    id: string;
    code: string;
    title: string;
    kind: string;
    status: string;
    year?: number | null;
    authorsOrEditors?: string | null;
    description?: string | null;
    externalArtifactNote?: string | null;
    tablesCount?: number;
};

type ReferenceTable = {
    id: string;
    referenceBookId: string;
    code: string;
    title: string;
    tableKind: string;
    description?: string | null;
    schemaJson?: ReferenceSchemaJson | null;
    status?: string;
    notes?: string | null;
    rowsCount?: number;
};

type ReferenceTableRowPayload = Record<string, unknown>;

type ReferenceTableRow = {
    id: string;
    referenceTableId: string;
    rowIndex: number;
    rowKind?: string | null;
    heatTreatment?: string | null;
    label: string;
    payload?: ReferenceTableRowPayload | null;
};

type ReferenceTableRowsPage = {
    items?: ReferenceTableRow[];
    total?: number;
    limit?: number;
    offset?: number;
};

const formatCellValue = (value: unknown, t: Translate): string => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    if (typeof value === 'boolean') {
        return value
            ? t('app.page.referenceBooks.boolean.true')
            : t('app.page.referenceBooks.boolean.false');
    }
    if (typeof value === 'number') {
        return Number.isInteger(value) ? String(value) : String(value);
    }
    return String(value);
};

const columnTitle = (column: ReferenceSchemaColumn, t: Translate): string => {
    const key = `app.page.referenceBooks.col.${column.name}`;
    const translated = t(key);
    if (translated && translated !== key) {
        return translated;
    }
    if (column.unit) {
        return `${column.name} (${column.unit})`;
    }
    return column.name;
};

const NUMERIC_NAME_RE = /(_pct|_mg|_ug|_kcal|_g|energy|moisture|protein|fat|carb|fiber|ash|sodium|potassium|calcium|magnesium|phosphorus|ferrum|zinc|cuprum|selenium|vitamin|cholesterol|sugar|fiber|thiamine|riboflavin|niacin|pyridoxine|folic|tocopherol|carotene)/i;

const isNumericColumn = (column: ReferenceSchemaColumn): boolean => {
    if (column.type === 'integer' || column.type === 'number') {
        return true;
    }
    if (column.unit) {
        return true;
    }
    if (column.maps_to) {
        return true;
    }
    return NUMERIC_NAME_RE.test(column.name || '');
};

const getSchemaColumns = (table: ReferenceTable | null): ReferenceSchemaColumn[] => {
    const columns = table?.schemaJson?.columns;
    return Array.isArray(columns) ? columns : [];
};

const orderDisplayColumns = (columns: ReferenceSchemaColumn[]): ReferenceSchemaColumn[] => {
    const priority = ['code', 'label'] as const;
    const head = priority
        .map((name) => columns.find((column) => column.name === name))
        .filter((column): column is ReferenceSchemaColumn => Boolean(column));
    const rest = columns.filter((column) => !priority.includes(column.name as typeof priority[number]));
    return [...head, ...rest];
};

const cellValueForColumn = (row: ReferenceTableRow, column: ReferenceSchemaColumn): unknown => {
    const payload: ReferenceTableRowPayload =
        row.payload && typeof row.payload === 'object' ? row.payload : {};
    if (column.name === 'label') {
        return row.label ?? payload.label;
    }
    if (column.name === 'row_kind') {
        return row.rowKind ?? payload.row_kind;
    }
    if (column.name === 'heat_treatment_hint') {
        return payload.heat_treatment_hint ?? row.heatTreatment;
    }
    if (Object.prototype.hasOwnProperty.call(payload, column.name)) {
        return payload[column.name];
    }
    return null;
};

const stickyColumnIndex = (
    columns: ReferenceSchemaColumn[],
    index: number,
    columnName: string,
): number | null => {
    if (!['code', 'label'].includes(columnName)) {
        return null;
    }
    return 1 + columns
        .slice(0, index)
        .filter((item) => ['code', 'label'].includes(item.name)).length;
};

const ReferenceBooksPage: React.FC = () => {
    const { i18n } = useTranslation();
    const t = i18n.t.bind(i18n) as Translate;
    const navigate = useNavigate();
    const { bookId, tableId } = useParams<{ bookId?: string; tableId?: string }>();
    const [loading, setLoading] = useState(false);
    const [books, setBooks] = useState<ReferenceBook[]>([]);
    const [book, setBook] = useState<ReferenceBook | null>(null);
    const [tables, setTables] = useState<ReferenceTable[]>([]);
    const [table, setTable] = useState<ReferenceTable | null>(null);
    const [rows, setRows] = useState<ReferenceTableRow[]>([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [query, setQuery] = useState('');

    const schemaColumns = useMemo(() => getSchemaColumns(table), [table]);
    const displayColumns = useMemo(
        () => orderDisplayColumns(schemaColumns.filter((column) => Boolean(column?.name))),
        [schemaColumns],
    );

    const loadBooks = useCallback(() => {
        setLoading(true);
        getFetchRequest(
            makeUrl([REFERENCE_BOOKS_URL]),
            (response: unknown) => {
                setBooks(Array.isArray(response) ? (response as ReferenceBook[]) : []);
                setLoading(false);
            },
            (err: unknown) => {
                setLoading(false);
                errorCallback(err);
            },
        );
    }, []);

    const loadBook = useCallback(() => {
        if (!bookId) {
            return;
        }
        setLoading(true);
        getFetchRequest(
            makeUrl([REFERENCE_BOOKS_URL, `${bookId}/`]),
            (response: ReferenceBook) => {
                setBook(response);
                getFetchRequest(
                    makeUrl([REFERENCE_BOOKS_URL, `${bookId}/table/`]),
                    (tablesResponse: unknown) => {
                        setTables(Array.isArray(tablesResponse) ? (tablesResponse as ReferenceTable[]) : []);
                        setLoading(false);
                    },
                    (err: unknown) => {
                        setLoading(false);
                        errorCallback(err);
                    },
                );
            },
            (err: unknown) => {
                setLoading(false);
                errorCallback(err);
            },
        );
    }, [bookId]);

    const loadRows = useCallback((nextOffset = 0, nextQuery = '') => {
        if (!tableId) {
            return;
        }
        setLoading(true);
        const params = new URLSearchParams();
        params.set('limit', String(PAGE_SIZE));
        params.set('offset', String(nextOffset));
        if (nextQuery.trim()) {
            params.set('q', nextQuery.trim());
        }
        getFetchRequest(
            makeUrl([REFERENCE_TABLE_URL, `${tableId}/`]),
            (tableResponse: ReferenceTable) => {
                setTable(tableResponse);
                getFetchRequest(
                    makeUrl([REFERENCE_TABLE_URL, `${tableId}/rows/?${params.toString()}`]),
                    (rowsResponse: ReferenceTableRowsPage) => {
                        setRows(rowsResponse?.items || []);
                        setTotal(rowsResponse?.total || 0);
                        setOffset(rowsResponse?.offset || 0);
                        setLoading(false);
                    },
                    (err: unknown) => {
                        setLoading(false);
                        errorCallback(err);
                    },
                );
            },
            (err: unknown) => {
                setLoading(false);
                errorCallback(err);
            },
        );
    }, [tableId]);

    useEffect(() => {
        if (tableId) {
            loadRows(0, '');
            setQuery('');
            return;
        }
        if (bookId) {
            loadBook();
            return;
        }
        loadBooks();
    }, [bookId, tableId, loadBooks, loadBook, loadRows]);

    const downloadCsv = () => {
        if (!tableId) {
            return;
        }
        getFetchRequest(
            makeUrl([REFERENCE_TABLE_URL, `${tableId}/csv/`]),
            async (response: Response) => {
                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = objectUrl;
                link.download = `${table?.code || 'reference-table'}.csv`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(objectUrl);
            },
            errorCallback,
            true,
        );
    };

    const onSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        loadRows(0, query);
    };

    const rowKindLabel = (kind: unknown): string => {
        if (kind === null || kind === undefined || kind === '') {
            return '—';
        }
        const kindText = String(kind);
        const key = `app.page.referenceBooks.rowKindValue.${kindText}`;
        const translated = t(key);
        return translated !== key ? translated : kindText;
    };

    if (tableId) {
        return (
            <section className="main__content workspace reference-books">
                <div className="reference-books__page-header">
                    <div className="reference-books__title-block">
                        <Title titleValue={table?.title || t('app.page.referenceBooks.tableTitle')} />
                        {table?.description ? (
                            <p className="block__content reference-books__hint">{table.description}</p>
                        ) : null}
                        {table?.schemaJson?.basis ? (
                            <p className="block__content reference-books__hint">
                                {t('app.page.referenceBooks.basis')}: {table.schemaJson.basis}
                            </p>
                        ) : null}
                    </div>
                    <div className="reference-books__header-actions">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate(`/reference-books/${bookId || table?.referenceBookId}`)}
                        >
                            {t('app.page.referenceBooks.backToBook')}
                        </Button>
                        <Button type="button" variant="outline" onClick={downloadCsv}>
                            {t('app.page.referenceBooks.downloadCsv')}
                        </Button>
                    </div>
                </div>

                <form className="reference-books__toolbar" onSubmit={onSearchSubmit}>
                    <div className="reference-books__search relative">
                        <SearchIcon nameOfClass="product__input-icon pointer-events-none absolute top-1/2 left-16 h-20 w-20 -translate-y-1/2" />
                        <Input
                            fullWidth
                            className="pl-40"
                            value={query}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
                            placeholder={t('app.page.referenceBooks.searchPlaceholder')}
                        />
                    </div>
                    <Button type="submit">{t('app.page.referenceBooks.applyFilters')}</Button>
                    <p className="block__content reference-books__total">
                        {t('app.page.referenceBooks.total')}: {total}
                    </p>
                </form>

                {loading ? <Loading /> : null}

                {!loading ? (
                    <>
                        <div className="reference-books__table-wrap">
                            <table className="reference-books__table reference-books__table_wide block__table">
                                <thead>
                                    <tr>
                                        <th className="reference-books__sticky reference-books__sticky_0">#</th>
                                        {displayColumns.map((column, index) => {
                                            const stickyIndex = stickyColumnIndex(displayColumns, index, column.name);
                                            return (
                                                <th
                                                    key={column.name}
                                                    className={
                                                        stickyIndex !== null
                                                            ? `reference-books__sticky reference-books__sticky_${stickyIndex}`
                                                            : undefined
                                                    }
                                                    title={column.description || column.maps_to || undefined}
                                                >
                                                    {columnTitle(column, t)}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => {
                                        const kind = row.rowKind || (row.payload?.row_kind as string | undefined);
                                        return (
                                            <tr
                                                key={row.id}
                                                className={
                                                    kind === 'category'
                                                        ? 'reference-books__row_category'
                                                        : kind === 'recipe'
                                                            ? 'reference-books__row_recipe'
                                                            : undefined
                                                }
                                            >
                                                <td className="reference-books__sticky reference-books__sticky_0">
                                                    {row.rowIndex}
                                                </td>
                                                {displayColumns.map((column, index) => {
                                                    const stickyIndex = stickyColumnIndex(
                                                        displayColumns,
                                                        index,
                                                        column.name,
                                                    );
                                                    const raw = cellValueForColumn(row, column);
                                                    const display = column.name === 'row_kind'
                                                        ? rowKindLabel(raw)
                                                        : formatCellValue(raw, t);
                                                    const cellClass = [
                                                        stickyIndex !== null
                                                            ? `reference-books__sticky reference-books__sticky_${stickyIndex}`
                                                            : '',
                                                        column.name === 'label'
                                                            || column.name === 'notes'
                                                            || column.name === 'food_category'
                                                            ? 'reference-books__cell_text'
                                                            : '',
                                                        isNumericColumn(column)
                                                            ? 'reference-books__cell_value'
                                                            : '',
                                                    ].filter(Boolean).join(' ');
                                                    return (
                                                        <td
                                                            key={`${row.id}-${column.name}`}
                                                            className={cellClass || undefined}
                                                        >
                                                            {display}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="reference-books__pagination">
                            <Button
                                type="button"
                                variant="outline"
                                disabled={offset <= 0}
                                onClick={() => loadRows(Math.max(0, offset - PAGE_SIZE), query)}
                            >
                                {t('app.page.referenceBooks.prev')}
                            </Button>
                            <span className="block__content">
                                {t('app.page.referenceBooks.pageRange', {
                                    from: total === 0 ? 0 : offset + 1,
                                    to: Math.min(offset + PAGE_SIZE, total),
                                    total,
                                })}
                            </span>
                            <Button
                                type="button"
                                variant="outline"
                                disabled={offset + PAGE_SIZE >= total}
                                onClick={() => loadRows(offset + PAGE_SIZE, query)}
                            >
                                {t('app.page.referenceBooks.next')}
                            </Button>
                        </div>
                    </>
                ) : null}
            </section>
        );
    }

    if (bookId) {
        return (
            <section className="main__content workspace reference-books">
                <div className="reference-books__page-header">
                    <div className="reference-books__title-block">
                        <Title titleValue={book?.title || t('app.page.referenceBooks.title')} />
                    </div>
                    <div className="reference-books__header-actions">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate('/reference-books')}
                        >
                            {t('app.page.referenceBooks.backToList')}
                        </Button>
                    </div>
                </div>

                {loading ? <Loading /> : null}

                {!loading && book ? (
                    <>
                        <article className="reference-books__card">
                            <dl className="reference-books__meta">
                                <div>
                                    <dt className="block__text">{t('app.page.referenceBooks.code')}</dt>
                                    <dd className="block__content">{book.code}</dd>
                                </div>
                                <div>
                                    <dt className="block__text">{t('app.page.referenceBooks.kind')}</dt>
                                    <dd className="block__content">{book.kind}</dd>
                                </div>
                                <div>
                                    <dt className="block__text">{t('app.page.referenceBooks.status')}</dt>
                                    <dd className="block__content">{book.status}</dd>
                                </div>
                                <div>
                                    <dt className="block__text">{t('app.page.referenceBooks.year')}</dt>
                                    <dd className="block__content">{book.year || '—'}</dd>
                                </div>
                                <div className="reference-books__meta_wide">
                                    <dt className="block__text">{t('app.page.referenceBooks.authors')}</dt>
                                    <dd className="block__content">{book.authorsOrEditors || '—'}</dd>
                                </div>
                            </dl>
                            {book.description ? (
                                <p className="block__content reference-books__description">{book.description}</p>
                            ) : null}
                            {book.externalArtifactNote ? (
                                <p className="block__content reference-books__note">
                                    {book.externalArtifactNote}
                                </p>
                            ) : null}
                        </article>

                        <h2 className="block__title-h3 reference-books__section-title">
                            {t('app.page.referenceBooks.tables')}
                        </h2>
                        <div className="reference-books__table-wrap">
                            <table className="reference-books__table block__table">
                                <thead>
                                    <tr>
                                        <th>{t('app.page.referenceBooks.code')}</th>
                                        <th>{t('app.page.referenceBooks.tableTitle')}</th>
                                        <th>{t('app.page.referenceBooks.tableKind')}</th>
                                        <th>{t('app.page.referenceBooks.rowsCount')}</th>
                                        <th aria-label={t('app.page.referenceBooks.open')} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {tables.map((item) => (
                                        <tr key={item.id}>
                                            <td>{item.code}</td>
                                            <td className="reference-books__cell_text">{item.title}</td>
                                            <td>{item.tableKind}</td>
                                            <td>{item.rowsCount}</td>
                                            <td className="reference-books__cell_action">
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="small"
                                                    onClick={() => navigate(`/reference-books/${bookId}/tables/${item.id}`)}
                                                >
                                                    {t('app.page.referenceBooks.open')}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : null}
            </section>
        );
    }

    return (
        <section className="main__content workspace reference-books">
            <div className="reference-books__page-header">
                <div className="reference-books__title-block">
                    <Title titleValue={t('app.page.referenceBooks.title')} />
                    <p className="block__content reference-books__hint">
                        {t('app.page.referenceBooks.listHint')}
                    </p>
                </div>
            </div>

            {loading ? <Loading /> : null}

            {!loading ? (
                <div className="reference-books__table-wrap">
                    <table className="reference-books__table block__table">
                        <thead>
                            <tr>
                                <th className="reference-books__col_code">{t('app.page.referenceBooks.code')}</th>
                                <th className="reference-books__cell_text">{t('app.page.referenceBooks.bookTitle')}</th>
                                <th className="reference-books__col_kind">{t('app.page.referenceBooks.kind')}</th>
                                <th>{t('app.page.referenceBooks.status')}</th>
                                <th className="reference-books__col_year">{t('app.page.referenceBooks.year')}</th>
                                <th>{t('app.page.referenceBooks.tablesCount')}</th>
                                <th className="reference-books__cell_action" aria-label={t('app.page.referenceBooks.open')} />
                            </tr>
                        </thead>
                        <tbody>
                            {books.map((item) => (
                                <tr key={item.id}>
                                    <td className="reference-books__col_code">{item.code}</td>
                                    <td className="reference-books__cell_text">{item.title}</td>
                                    <td className="reference-books__col_kind">{item.kind}</td>
                                    <td>{item.status}</td>
                                    <td className="reference-books__col_year">{item.year || '—'}</td>
                                    <td>{item.tablesCount}</td>
                                    <td className="reference-books__cell_action">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="small"
                                            onClick={() => navigate(`/reference-books/${item.id}`)}
                                        >
                                            {t('app.page.referenceBooks.open')}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </section>
    );
};

export default ReferenceBooksPage;
